import type {ChangeEvent, FocusEvent, KeyboardEvent, ReactElement} from 'react';
import type {Editor} from 'grapesjs';
import {useCallback, useContext, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EmailMakerMode} from '@EmailMakerReact/email-maker-react.types';
import {NEW_TEMPLATE_NAME, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {getMainWrapper} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {TEMPLATE_NAME_MAX_LENGTH} from './template-name-input.types';
import styles from './template-name-input.module.scss';

/** Rename only sets the name on the GrapesJS wrapper; the next autosave/manual save persists it. */
export function TemplateNameInput(): ReactElement {
  const {store, editor} = useContext(emailMakerContext);
  const previousNameRef = useRef<string>(store.templateName || NEW_TEMPLATE_NAME);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputSize, setInputSize] = useState<number>(() => Math.max(previousNameRef.current.length, 1));

  const isLocked = store.isApproved && store.mode === EmailMakerMode.LOAD;

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    setInputSize(Math.max(event.currentTarget.value.length, 1));
  }, []);

  const commitRename = useCallback(
    (rawValue: string): void => {
      const trimmed = rawValue.trim();
      if (!trimmed) {
        if (inputRef.current) {
          inputRef.current.value = previousNameRef.current;
        }
        return;
      }
      previousNameRef.current = trimmed;
      pushNameToEditor(editor, trimmed);
    },
    [editor]
  );

  const handleFocus = useCallback((): void => {
    inputRef.current?.select();
  }, []);

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>): void => {
      commitRename(event.currentTarget.value);
    },
    [commitRename]
  );

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    } else if (event.key === 'Escape') {
      event.currentTarget.value = previousNameRef.current;
      setInputSize(Math.max(previousNameRef.current.length, 1));
      event.currentTarget.blur();
    }
  }, []);

  const handlePencilClick = useCallback((): void => {
    if (!isLocked) {
      inputRef.current?.focus();
    }
  }, [isLocked]);

  return (
    <div className={styles.wrapper}>
      <input
        aria-label={window.i18next.t('pmwjs_template_name')}
        className={styles.input}
        defaultValue={previousNameRef.current}
        disabled={isLocked}
        maxLength={TEMPLATE_NAME_MAX_LENGTH}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        ref={inputRef}
        size={inputSize}
        type="text"
      />
      <Icon ariaHidden className={styles.editIcon} icon="icon-pencil" onClick={handlePencilClick} size={IconSize.SIZE_ICON_20} />
    </div>
  );
}

function pushNameToEditor(editor: Editor | null, name: string): void {
  if (!editor) {
    return;
  }
  const wrapper = getMainWrapper(editor);
  if (wrapper) {
    wrapper.set(EMAIL_MAKER_PROPS.TEMPLATE_NAME, name);
  }
}
